import { type ReactNode, useEffect, useRef } from "react";
import { DrawerToggleButton } from "./DrawerToggleButton";

interface CollapsibleDrawerProps {
  isCollapsed: boolean;
  onCollapsedChange: (isCollapsed: boolean) => void;
  /** Noun for the toggles' aria-labels, e.g. "refine panel". */
  panel: string;
  /** Tailwind width class for the open drawer, e.g. "w-[320px]". */
  expandedWidth: string;
  /** Extra classes for the aside, e.g. "h-full". */
  className?: string;
  /** Classes for the collapsed rail around the expand toggle. */
  railClassName?: string;
  "aria-label"?: string;
  /** Renders the open contents; place ``collapseToggle`` in their header. */
  children: (collapseToggle: ReactNode) => ReactNode;
}

/**
 * A side drawer that collapses to a 72px rail holding its expand toggle.
 *
 * The contents stay mounted at their expanded width while the aside clips
 * them, so collapsing keeps their state and textareas never lay out at 72px.
 * While collapsed they are hidden and inert, which would leave a keyboard user
 * focused on nothing: so after a toggle the drawer moves focus to the toggle
 * that is now visible. Only its own toggles do that; collapsing from elsewhere
 * leaves focus alone.
 */
export function CollapsibleDrawer({
  isCollapsed,
  onCollapsedChange,
  panel,
  expandedWidth,
  className = "",
  railClassName = "flex px-6 py-6",
  "aria-label": ariaLabel,
  children,
}: CollapsibleDrawerProps) {
  const expandToggle = useRef<HTMLButtonElement>(null);
  const collapseToggle = useRef<HTMLButtonElement>(null);
  const isTogglingFromButton = useRef(false);

  useEffect(() => {
    if (!isTogglingFromButton.current) return;
    isTogglingFromButton.current = false;
    (isCollapsed ? expandToggle : collapseToggle).current?.focus();
  }, [isCollapsed]);

  const toggle = (collapsed: boolean) => {
    isTogglingFromButton.current = true;
    onCollapsedChange(collapsed);
  };

  return (
    <aside
      aria-label={ariaLabel}
      className={`relative shrink-0 overflow-hidden bg-[var(--bg-modal)] transition-[width] duration-300 ease-out ${
        isCollapsed ? "w-[72px]" : expandedWidth
      } ${className}`}
    >
      {isCollapsed && (
        <div className={railClassName}>
          <DrawerToggleButton
            ref={expandToggle}
            direction="expand"
            panel={panel}
            onClick={() => toggle(false)}
          />
        </div>
      )}
      <div
        className={`flex h-full flex-col ${expandedWidth} ${
          isCollapsed
            ? "pointer-events-none invisible absolute inset-y-0 left-0"
            : ""
        }`}
        aria-hidden={isCollapsed}
        {...(isCollapsed ? { inert: true } : {})}
      >
        {children(
          <DrawerToggleButton
            ref={collapseToggle}
            direction="collapse"
            panel={panel}
            onClick={() => toggle(true)}
          />,
        )}
      </div>
    </aside>
  );
}
